"use client"

import { useState, useEffect } from "react"
import DashboardLayout from "../../components/Layout/DashboardLayout"
import Breadcrumbs from "../../components/Layout/Breadcrumbs"
import { Card, Button, Table, Badge } from "react-bootstrap"
import { Link } from "react-router-dom"
import api from "../../services/api"
import MiniFooter from "../../components/Layout/miniFooter"
import ConfirmModal from "../../components/links/ConfirmModal"
import ToastNotification from "../../components/Dashboard/ToastNotification"
import { FaDownload, FaQrcode, FaInfoCircle, FaLink, FaExternalLinkAlt, FaTrash } from "react-icons/fa"
import WhatsAppChat from "../../components/support/WhatsAppChat"

export default function QrCodesPage() {
  const breadcrumbItems = [{ name: "Dashboard", path: "/user-dashboard" }, { name: "Dynamic QR Codes" }]

  const [qrLinks, setQrLinks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [confirmState, setConfirmState] = useState({
      show: false,
      payload: null,
    })

  const [deleting, setDeleting] = useState(false)
  const [toastMessage, setToastMessage] = useState("")

// Toggle single checkbox
const toggleSelectLink = (id) => {
  setSelectedLinks((prev) =>
    prev.includes(id) ? prev.filter((linkId) => linkId !== id) : [...prev, id]
  );
};

// Select all
const toggleSelectAll = () => {
  if (selectedLinks.length === qrLinks.length) setSelectedLinks([]);
  else setSelectedLinks(qrLinks.map((link) => link._id));
};


  useEffect(() => {
    const fetchQrLinks = async () => {
      try {
        const res = await api.get("/links/v2-q")
        const qrData = res.data?.data
        setQrLinks(Array.isArray(qrData) ? qrData : [])
      } catch (err) {
        console.error(err)
        setError(err.response?.data?.message || "Failed to fetch QR links")
      } finally {
        setLoading(false)
      }
    }
    fetchQrLinks()
  }, [])

const requestDelete = (id) => {
      setConfirmState({
        show: true,
        payload: id,
      })
    }

const requestBulkDelete = () => {
  if (selectedLinks.length === 0) return

  setConfirmState({
    show: true,
    payload: selectedLinks,
  })
}


 const confirmDelete = async () => {
  if (!confirmState.payload) return

  setDeleting(true)

  try {
    const ids = Array.isArray(confirmState.payload)
      ? confirmState.payload
      : [confirmState.payload]

    await Promise.all(ids.map((id) => api.delete(`/links/${id}`)))

    setQrLinks((prev) =>
      prev.filter((link) => !ids.includes(link._id))
    )

    setSelectedLinks([])
    setToastMessage("Link(s) deleted successfully!")
  } catch (err) {
    console.error(err)
    setToastMessage("Failed to delete link(s)")
  } finally {
    setDeleting(false)
    setConfirmState({ show: false, payload: null })
  }
}


const handleDownload = async (fileUrl, filename = "qr-code.png") => {
  try {
    const response = await fetch(fileUrl)
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()

    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } catch (err) {
    console.error("Download failed", err)
  }
}



  // Skeleton loader for table rows
  const SkeletonRow = () => (
    <tr>
      <td style={{ padding: "16px" }}>
        <div style={{ height: "16px", width: "200px", background: "#e2e8f0", borderRadius: "4px" }} />
      </td>
      <td style={{ padding: "16px" }}>
        <div style={{ height: "64px", width: "64px", background: "#e2e8f0", borderRadius: "8px" }} />
      </td>
      <td style={{ padding: "16px" }}>
        <div style={{ height: "16px", width: "100px", background: "#e2e8f0", borderRadius: "4px" }} />
      </td>
      <td style={{ padding: "16px" }}>
        <div style={{ height: "32px", width: "100px", background: "#e2e8f0", borderRadius: "6px" }} />
      </td>
    </tr>
  )

  return (
    <DashboardLayout>
      <div
        style={{
          minHeight: "100vh",
          background: "#f8fafc",
        }}
      >
        <div className="container mt-4" style={{ maxWidth: "1200px" }}>
          <Breadcrumbs items={breadcrumbItems} />

          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div>
              <h3 className="mb-1 fw-bold d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "36px",
                    height: "36px",
                    background: "#3b82f6",
                    borderRadius: "8px",
                    color: "white",
                  }}
                >
                  <FaQrcode size={18} />
                </span>
                Dynamic QR Codes
              </h3>
              <p className="mb-0" style={{ color: "#64748b" }}>
                Manage all your custom-branded QR codes in one place
              </p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/qr-codes/create">
                <Button
                  className="d-flex align-items-center gap-2"
                  style={{
                    background: "#3b82f6",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#2563eb"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#3b82f6"
                  }}
                >
                  <FaQrcode /> Create QR Code
                </Button>
              </Link>
              <Link to="/dashboard/urls">
                <Button
                  variant="outline-secondary"
                  className="d-flex align-items-center gap-2"
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                    borderColor: "#e2e8f0",
                    color: "#64748b",
                    background: "white",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#3b82f6"
                    e.currentTarget.style.color = "#3b82f6"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0"
                    e.currentTarget.style.color = "#64748b"
                  }}
                >
                  <FaLink /> Manage Short URLs
                </Button>
              </Link>
            </div>
          </div>

          {/* Info Card */}
          <Card
            className="mb-4"
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              background: "#f0f9ff",
            }}
          >
            <Card.Body className="p-4">
              <h6 className="d-flex align-items-center gap-2 mb-3" style={{ color: "#0369a1", fontWeight: "600" }}>
                <FaInfoCircle /> How this works
              </h6>
              <ol className="mb-0 ps-3" style={{ color: "#475569", lineHeight: "1.8" }}>
                <li className="mb-2">
                  This page lists <strong>custom-branded QR codes</strong> only, including those generated using a
                  Branding Template when creating a short URL.
                </li>
                <li>
                  <strong>Standard QR codes</strong> are automatically generated for every short URL and can be
                  downloaded from the{" "}
                  <Link
                    to="/dashboard/urls"
                    style={{
                      color: "#3b82f6",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    Manage Short URL(s)
                  </Link>{" "}
                  page under the QR Code column.
                </li>
              </ol>
            </Card.Body>
          </Card>

          {/* QR Links Table Card */}
          <Card
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              background: "white",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-4">
              <h5 className="mb-4 fw-semibold" style={{ color: "#0f172a" }}>
                Your QR Links
              </h5>

              {loading ? (
                <Table responsive className="align-middle mb-0">
                  <thead style={{ background: "#f8fafc" }}>
                    <tr>
                      <th
                        style={{
                          padding: "14px 16px",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        Original URL
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        QR Preview
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        Created At
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3].map((i) => (
                      <SkeletonRow key={i} />
                    ))}
                  </tbody>
                </Table>
              ) : error ? (
                <div
                  className="d-flex align-items-center gap-3 p-4"
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    color: "#dc2626",
                  }}
                >
                  <FaInfoCircle size={20} />
                  <span>{error}</span>
                </div>
              ) : qrLinks.length === 0 ? (
                <div className="text-center py-5">
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "80px",
                      height: "80px",
                      background: "#f1f5f9",
                      borderRadius: "16px",
                      marginBottom: "16px",
                    }}
                  >
                    <FaQrcode size={36} style={{ color: "#94a3b8" }} />
                  </div>
                  <p style={{ color: "#64748b", marginBottom: "16px" }}>No QR codes found yet.</p>
                  <Link to="/qr-codes/create">
                    <Button
                      style={{
                        background: "#3b82f6",
                        border: "none",
                        padding: "10px 24px",
                        borderRadius: "8px",
                        fontWeight: "500",
                      }}
                    >
                      <FaQrcode className="me-2" /> Create Your First QR Code
                    </Button>
                  </Link>
                </div>
              ) : (
                
                <Table responsive className="align-middle mb-0">
                  <thead style={{ background: "#f8fafc" }}>
                    <tr>
                      <th style={{
                          padding: "14px 16px",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #e2e8f0",
                        }}>
                        <input
                          type="checkbox"
                          checked={selectedLinks.length === qrLinks.length && qrLinks.length > 0}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        Original URL
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        QR Preview
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        Created At
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          fontWeight: "600",
                          color: "#64748b",
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {qrLinks.map((link) => (
                      <tr
                        key={link._id}
                        style={{ transition: "all 0.15s ease" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f8fafc"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent"
                        }}
                      >
                      <td style={{ padding: "16px", borderBottom: "1px solid #f1f5f9" }}>
                        <input
                          type="checkbox"
                          checked={selectedLinks.includes(link._id)}
                          onChange={() => toggleSelectLink(link._id)}
                        />
                      </td>
                        <td style={{ padding: "16px", borderBottom: "1px solid #f1f5f9" }}>
                          <div className="d-flex align-items-center gap-2">
                            <FaExternalLinkAlt size={12} style={{ color: "#94a3b8" }} />
                            <span
                              style={{
                                maxWidth: "280px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "#334155",
                              }}
                            >
                              {link.originalUrl}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: "16px", borderBottom: "1px solid #f1f5f9" }}>
                          {link.qr?.fileUrl ? (
                            <img
                              src={link.qr.fileUrl || "/placeholder.svg"}
                              alt="QR Code"
                              style={{
                                width: 64,
                                height: 64,
                                borderRadius: 8,
                                border: "1px solid #e2e8f0",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.05)"
                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)"
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)"
                                e.currentTarget.style.boxShadow = "none"
                              }}
                            />
                          ) : (
                            <Badge
                              style={{
                                padding: "6px 12px",
                                borderRadius: "6px",
                                fontWeight: "500",
                                background: "#f1f5f9",
                                color: "#64748b",
                              }}
                            >
                              N/A
                            </Badge>
                          )}
                        </td>
                        <td style={{ padding: "16px", color: "#64748b", borderBottom: "1px solid #f1f5f9" }}>
                          {new Date(link.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: "16px", borderBottom: "1px solid #f1f5f9" }}>
  {link.qr?.fileUrl ? (
    <div className="d-flex gap-2">
      {/* Download button */}
<Button
  size="sm"
  className="d-flex align-items-center gap-2"
  style={{
    borderRadius: "6px",
    padding: "8px 14px",
    fontWeight: "500",
    border: "1px solid #3b82f6",
    color: "#3b82f6",
    background: "transparent",
    transition: "all 0.2s ease",
  }}
  onClick={() =>
    handleDownload(
      link.qr.fileUrl,
      `u2lr-qr-${link.shortCode}.png`
    )
  }
  onMouseEnter={(e) => {
    e.currentTarget.style.background = "#3b82f6"
    e.currentTarget.style.color = "white"
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = "transparent"
    e.currentTarget.style.color = "#3b82f6"
  }}
>
  <FaDownload />
</Button>

      {/* Delete button */}
      <Button
        size="sm"
        variant="outline-danger"
        onClick={() => requestDelete(link._id)}
      >
        <FaTrash />
      </Button>
    </div>
  ) : (
    <Badge
      style={{
        padding: "6px 12px",
        borderRadius: "6px",
        fontWeight: "500",
        background: "#f1f5f9",
        color: "#64748b",
      }}
    >
      No QR
    </Badge>
  )}
</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                
              )}
              
            </Card.Body>
          </Card>
          <br/>
          <div>
          {selectedLinks.length > 0 && (
            <Button
              variant="danger"
              size="sm"
              className="mb-2"
              onClick={requestBulkDelete}
            >
              Delete Selected ({selectedLinks.length})
            </Button>
          )}
          </div>
        </div>
        
      </div>

      <ConfirmModal
              show={confirmState.show}
              title="Confirm deletion"
              message="Are you sure you want to delete this link?"
              confirmText="Delete"
              loading={deleting}
              onCancel={() => setConfirmState({ show: false, payload: null })}
              onConfirm={confirmDelete}
            />

        <ToastNotification message={toastMessage} duration={3000} onClose={() => setToastMessage("")} />
        <WhatsAppChat position="right" />
      <MiniFooter />
    </DashboardLayout>
  )
}